// Login — email/password validated against TMCHub by the backend, which then
// issues the tmcchat.auth cookie. In Development a dev sign-in shortcut lets you
// exercise the shell without a reachable TMCHub.

import { useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AuthApi } from "../hooks/useApi.js";
import { useAuth } from "../auth/AuthProvider.jsx";
import ArcadeScene from "../components/ArcadeScene.jsx";

const isDev = import.meta.env.DEV;

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("signin"); // signin | forgot | sent
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const notice = useLocation().state?.notice; // e.g. back from /reset-password
  const [params] = useSearchParams();
  const returnUrl = params.get("returnUrl") || "/";
  // Cyberpunk gets the design's "secure uplink" jack-in screen (labels + chrome differ).
  const cyber = typeof document !== "undefined" && document.documentElement.dataset.theme === "cyberpunk";

  async function submit(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await AuthApi.login(email, password);
      await refresh();
      try { sessionStorage.setItem("tmc.boot", "1"); } catch { /* non-fatal */ }
      navigate(returnUrl, { replace: true });
    } catch (err) {
      setError(err.status === 401 ? "Invalid email or password." : "Could not sign in. Try again shortly.");
    } finally {
      setBusy(false);
    }
  }

  async function sendReset(e) {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await AuthApi.forgot(email);
      setMode("sent");
    } catch (err) {
      setError(err.body?.error || "Could not send the reset email. Try again shortly.");
    } finally {
      setBusy(false);
    }
  }

  function switchMode(next) { setError(""); setMode(next); }

  async function devSignIn(role) {
    setBusy(true);
    try {
      await AuthApi.devSignIn(email || `${role}@dev.local`, role);
      await refresh();
      try { sessionStorage.setItem("tmc.boot", "1"); } catch { /* non-fatal */ }
      navigate(returnUrl, { replace: true });
    } catch {
      setError("Dev sign-in failed.");
    } finally {
      setBusy(false);
    }
  }

  if (mode !== "signin") {
    return (
      <LoginShell onSubmit={sendReset} cyber={cyber}>
        <p className="login__tagline">Reset your TMCHub password</p>
        {mode === "sent" ? (
          <div className="login__notice">
            If <b>{email}</b> has an account, a reset link is on its way. It works once and expires in 24 hours.
          </div>
        ) : (
          <>
            <div className="field">
              <label className="field__label">Email</label>
              <input className="input" type="email" autoComplete="username" value={email} autoFocus
                     onChange={(e) => setEmail(e.target.value)} required />
            </div>
            {error && <div className="field__err">⚠ {error}</div>}
            <button className="btn btn--primary btn--block" type="submit" disabled={busy}>
              {busy ? <span className="btn__spin" /> : "Email me a reset link"}
            </button>
          </>
        )}
        <button type="button" className="login__link" onClick={() => switchMode("signin")}>← Back to sign in</button>
      </LoginShell>
    );
  }

  return (
    <LoginShell onSubmit={submit} cyber={cyber}>
        <p className="login__tagline">{cyber ? "THE MEDIA CROWD · NEURAL RELAY" : "Sign in with your TMCHub account"}</p>
        {notice && <div className="login__notice">{notice}</div>}

        <div className="field">
          <label className="field__label">{cyber ? "Operator ID" : "Email"}</label>
          <input className="input" type="email" autoComplete="username" value={email}
                 onChange={(e) => setEmail(e.target.value)} required />
        </div>
        <div className="field">
          <label className="field__label">{cyber ? "Passkey" : "Password"}</label>
          <input className="input" type="password" autoComplete="current-password" value={password}
                 onChange={(e) => setPassword(e.target.value)} required />
        </div>

        {error && <div className="field__err">⚠ {error}</div>}

        <button className="btn btn--primary btn--block" type="submit" disabled={busy}>
          {busy ? <span className="btn__spin" /> : (cyber ? "⚡ Jack in" : "Sign in")}
        </button>
        <button type="button" className="login__link" onClick={() => switchMode("forgot")}>Forgot password?</button>
        <p className="login__alt">New here? <Link to="/register">Request access</Link></p>

        {cyber && <div className="login__footer">◜ ENCRYPTED · TMCHUB IDENTITY VERIFIED ◞</div>}

        {isDev && (
          <div className="login__dev">
            <span>Dev shortcuts:</span>
            <button type="button" className="btn btn--soft btn--sm" onClick={() => devSignIn("staff")} disabled={busy}>as Staff</button>
            <button type="button" className="btn btn--soft btn--sm" onClick={() => devSignIn("client")} disabled={busy}>as Client</button>
          </div>
        )}
    </LoginShell>
  );
}

// The sign-in screen's backdrop + card, shared with /reset-password.
export function LoginShell({ onSubmit, cyber, children }) {
  return (
    <div className="login">
      <ArcadeScene />
      <div className="brand-bg" aria-hidden>
        <span className="brand-bg__orb brand-bg__orb--1" />
        <span className="brand-bg__orb brand-bg__orb--2" />
        <span className="brand-bg__orb brand-bg__orb--3" />
        <span className="brand-bg__grid" />
      </div>

      <form className="login__card" onSubmit={onSubmit}>
        {cyber && (
          <>
            <span className="login__corner login__corner--tl" aria-hidden />
            <span className="login__corner login__corner--tr" aria-hidden />
            <span className="login__corner login__corner--bl" aria-hidden />
            <span className="login__corner login__corner--br" aria-hidden />
            <div className="login__eyebrow">SECURE UPLINK // v2.077</div>
          </>
        )}
        <div className="login__brand">
          <span className="tmc-mark tmc-mark--lg">
            <span className="tmc-mark__glyph" />
            <span className="tmc-mark__word">TMC<b>Chat</b></span>
          </span>
        </div>
        {children}
      </form>
    </div>
  );
}
